import { Formik, ErrorMessage } from 'formik';
import SignaturePad from 'components/SignaturePad';
import { CheckboxCard, SectionLegend, StepShell } from 'components/Profile';
import { blueCheckboxCardClass, blueCheckboxClass, choiceBtnUnselected } from 'components/Profile/helper';
import { getInitialValues, getValidationSchema, allAccepted, setAllConsents, isSigned, pendingForms, signedForms, consentPayload } from './helper';
import ConsentModal from './Modal';
import Signed from './Signed';
import Documents from './Documents';
import { getPatient } from 'library/auth';
import { connect } from 'react-redux';
import { ProfileActions } from 'store/actions';
import toast from 'react-hot-toast';
import { useOutletContext } from 'react-router-dom';
import { useState } from 'react';
import { FileText } from 'lucide-react';

const enhancer = connect(null, { ...ProfileActions })

// Shared by the Consent step (form_type 'global') and the HIPAA step (form_type 'hipaa').
const Form = ({ createConsent, stepId = 'consent', formType = 'global', children = null }) => {
  const { sectionData, mergeSection, goToNextStep }: any = useOutletContext();
  const [activeConsent, setActiveConsent] = useState(null);
  const consentForms = sectionData[stepId] || [];
  const signed = isSigned(consentForms);
  const pending = pendingForms(consentForms);
  const signedDocuments = signedForms(consentForms);
  const patientName = getPatient()?.full_name;

  const handleSubmit = async (values) => {
    const consentFormIds = pending.filter(({ id }) => values.consents[id]).map(({ id }) => id);

    if (!consentFormIds.length) return goToNextStep(stepId);

    try {
      const { data } = await createConsent(consentPayload(consentFormIds, formType, values.signature));

      toast.success('Consent forms saved successfully');
      mergeSection(stepId, consentForms.map((form) => (data || []).find(({ id }) => id === form.id) || form));
      goToNextStep(stepId);

    } catch (error: any) {
      toast.error(error.errors || 'Failed to save consent forms. Please try again later.');
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(pending)}
      validationSchema={getValidationSchema(pending)}
      onSubmit={handleSubmit}
    >
      {({ values, setValues, setFieldValue, isSubmitting }) => {
        const setConsent = (id, checked) => {
          const consents = { ...values.consents, [id]: checked };
          setValues({ ...values, consents, accept_all: allAccepted(consents, pending) });
        }

        return (
          <StepShell stepId={stepId} isSubmitting={isSubmitting} viewOnly={signed}>
            {signed ? (
              <Signed consentForms={consentForms} patientName={patientName} onView={setActiveConsent} />
            ) : (
              <>
                {pending.length > 1 && (
                  <CheckboxCard
                    name="accept_all"
                    label={`I agree to all ${pending.length} forms`}
                    description="Standard privacy, treatment and billing terms. Read any of them below."
                    className={`${blueCheckboxCardClass} mb-5`}
                    checkboxClassName={blueCheckboxClass}
                    labelClassName="text-[15px] font-bold text-secondary-900"
                    onChange={({ target: { checked } }) => setValues({
                      ...values,
                      accept_all: checked,
                      consents: setAllConsents(pending, checked)
                    })}
                  />
                )}

                <SectionLegend>Required agreements</SectionLegend>

                <div className="mb-5 space-y-3">
                  {pending.map((consentForm) => {
                    const { id, attributes: { title, description } } = consentForm;

                    return (
                      <CheckboxCard
                        key={id}
                        name={`consents.${id}`}
                        label={title}
                        description={description}
                        className={blueCheckboxCardClass}
                        checkboxClassName={blueCheckboxClass}
                        labelClassName="font-bold text-secondary-900"
                        onChange={({ target: { checked } }) => setConsent(id, checked)}
                        action={
                          <button
                            type="button"
                            onClick={() => setActiveConsent(consentForm)}
                            className={`btn btn-sm h-9 gap-2 rounded-[10px] font-semibold ${choiceBtnUnselected}`}
                          >
                            <FileText size={15} strokeWidth={2.2} /> View consent
                          </button>
                        }
                      />
                    )
                  })}
                </div>

                <SectionLegend>Your signature</SectionLegend>

                <div className="mb-5">
                  <SignaturePad signature={values.signature} onSignature={(file) => setFieldValue('signature', file)} />
                  <ErrorMessage name="signature" render={(msg) => <p className="text-error text-xs mt-1 ml-1">{msg}</p>} />
                </div>

                {signedDocuments.length > 0 && (
                  <>
                    <SectionLegend>Your signed documents</SectionLegend>

                    <Documents consentForms={signedDocuments} onView={setActiveConsent} />
                  </>
                )}
              </>
            )}

            {children && <div className="mt-6">{children}</div>}

            {activeConsent && (
              <ConsentModal
                consentForm={activeConsent}
                onClose={() => setActiveConsent(null)}
                onAgree={() => {
                  setConsent(activeConsent.id, true);
                  setActiveConsent(null);
                }}
              />
            )}
          </StepShell>
        )
      }}
    </Formik>
  )
}

export default enhancer(Form);
